/**
 * IndiaMandiLogo — custom SVG emblem.
 *
 * A rounded emerald square with a rangoli-inspired ring, a pair of
 * crossed wheat ears in warm amber, a saffron seed dot at the top,
 * and four rangoli corner dots.
 */
export function IndiaMandiLogo({ size = 40, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-label="India Mandi">
      <defs>
        <linearGradient id="im-emerald" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#064E3B" />
          <stop offset="1" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="im-amber" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#EA580C" />
          <stop offset="1" stopColor="#FBBF24" />
        </linearGradient>
        <radialGradient id="im-inner" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(255,255,255,0.14)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>

      {/* base tile */}
      <rect x="2" y="2" width="96" height="96" rx="24" fill="url(#im-emerald)" />
      <rect x="2" y="2" width="96" height="96" rx="24" fill="url(#im-inner)" />

      {/* concentric rings */}
      <circle cx="50" cy="50" r="34" stroke="rgba(255,255,255,0.14)" strokeWidth="1" fill="none" />
      <circle cx="50" cy="50" r="28" stroke="rgba(251,191,36,0.35)" strokeDasharray="1 3" strokeWidth="0.8" fill="none" />

      {/* Two crossed wheat ears */}
      <g transform="translate(50 66)">
        <WheatEar rotate={-22} />
        <WheatEar rotate={22} />
        {/* central seed */}
        <circle cx="0" cy="-18" r="3.2" fill="#FDE68A" />
        <circle cx="0" cy="-18" r="1.6" fill="#EA580C" />
      </g>

      {/* rangoli corner dots */}
      <g fill="#FBBF24" opacity="0.75">
        <circle cx="18" cy="18" r="1.6" />
        <circle cx="82" cy="18" r="1.6" />
        <circle cx="18" cy="82" r="1.6" />
        <circle cx="82" cy="82" r="1.6" />
      </g>
    </svg>
  );
}

function WheatEar({ rotate = 0 }) {
  return (
    <g transform={`rotate(${rotate})`}>
      {/* stem */}
      <path d="M0 2 L0 -36" stroke="url(#im-amber)" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      {/* grains — 6 pairs */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <g key={i} transform={`translate(0 ${-8 - i * 4.6})`}>
          <ellipse cx="-3.4" cy="0" rx="1.8" ry="3.3" fill="url(#im-amber)" />
          <ellipse cx="3.4" cy="0" rx="1.8" ry="3.3" fill="url(#im-amber)" />
        </g>
      ))}
    </g>
  );
}

/**
 * IndiaMandiMark — the emblem + INDIA MANDI wordmark, standard lockup.
 */
export function IndiaMandiMark({ size = 40, invert = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <IndiaMandiLogo size={size} />
      <div className="leading-tight">
        <div className={`font-display font-black tracking-tight ${invert ? "text-white" : "text-emerald-950"}`} style={{ fontSize: size * 0.44 }}>
          INDIA MANDI
        </div>
        <div className={`font-mono uppercase tracking-[0.22em] ${invert ? "text-orange-300" : "text-orange-600"}`} style={{ fontSize: size * 0.22 }}>
          Mandi.GLCZone.in
        </div>
      </div>
    </div>
  );
}
